import { useEffect, useState } from 'react';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import { fetchRemoteConfig, type RemoteConfig } from '@/lib/api/remoteConfig';
import { supabase } from '@/integrations/supabase/client';
import { getSupabaseAccessTokenSnapshot } from '@/integrations/supabase/sessionCookies';

interface UseRemoteConfigParams {
  tenantSlug?: string | null;
  locationId?: string | null;
  enabled?: boolean;
  queryOptions?: Omit<UseQueryOptions<RemoteConfig>, 'queryKey' | 'queryFn' | 'enabled'>;
}

export const useRemoteConfigQuery = ({
  tenantSlug,
  locationId,
  enabled,
  queryOptions,
}: UseRemoteConfigParams = {}): UseQueryResult<RemoteConfig> => {
  const hasSupabaseAccessToken = useSupabaseAccessTokenReady();
  const queryKey = ['remote-config', tenantSlug ?? null, locationId ?? null];
  return useQuery({
    queryKey,
    queryFn: () =>
      fetchRemoteConfig({
        tenantSlug,
        locationId,
      }),
    enabled: Boolean(tenantSlug) && hasSupabaseAccessToken && (enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
};

const useSupabaseAccessTokenReady = (): boolean => {
  const [ready, setReady] = useState<boolean>(() => Boolean(getSupabaseAccessTokenSnapshot()));

  useEffect(() => {
    let mounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        if (data.session?.access_token) {
          setReady(true);
        }
      })
      .catch(() => {
        /* ignore */
      });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setReady(Boolean(session?.access_token || getSupabaseAccessTokenSnapshot()));
    });

    return () => {
      mounted = false;
      listener?.subscription.unsubscribe();
    };
  }, []);

  return ready;
};
